import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { DeviceTokenApi } from "./device-token-api";
import {
  requestPermission,
  getToken,
  deleteToken,
  getDevicePlatform,
  unregisterDeviceForRemoteMessages,
  AUTHORIZATION_STATUS,
  getPermissionStatus,
} from "./messaging-service";
import type { PermissionStatus, PushNotificationStatus } from "./types";

export interface UseDeviceTokenOptions {
  deviceTokenApi: DeviceTokenApi;
  /** When true the status query is enabled. Typically gated on auth readiness. */
  enabled?: boolean;
}

export const STATUS_QUERY_KEY = ["push-notification-status"];

/**
 * Remove the device token from the backend, delete the local FCM token,
 * and unregister the device for remote messages. Each step runs
 * independently so a failure in one doesn't prevent the others.
 */
export async function teardownDeviceToken(deviceTokenApi: DeviceTokenApi): Promise<void> {
  try {
    const token = await getToken();
    await deviceTokenApi.unregisterToken(token);
  } catch (error: unknown) {
    console.warn("[Push] Failed to unregister token from backend:", error);
  }

  try {
    await deleteToken();
  } catch (error: unknown) {
    console.warn("[Push] Failed to delete local FCM token:", error);
  }

  try {
    await unregisterDeviceForRemoteMessages();
  } catch (error: unknown) {
    console.warn("[Push] Failed to unregister device for remote messages:", error);
  }
}

/**
 * React Query hook for all device-token operations: status query,
 * register mutation (request permission → get token → register),
 * and unregister mutation (unregister → delete local token).
 */
export function useDeviceToken({ deviceTokenApi, enabled = true }: UseDeviceTokenOptions) {
  const queryClient = useQueryClient();

  const isRegistered = useQuery({
    queryKey: STATUS_QUERY_KEY,
    queryFn: async (): Promise<PushNotificationStatus> => {
      const status = await getPermissionStatus();

      if (status !== AUTHORIZATION_STATUS.AUTHORIZED && status !== AUTHORIZATION_STATUS.PROVISIONAL) {
        const permission: PermissionStatus = status === AUTHORIZATION_STATUS.DENIED ? "denied" : "undetermined";
        return { permission, registered: false };
      }

      const token = await getToken();
      const registered = await deviceTokenApi.isRegistered(token);
      return { permission: "granted", registered };
    },
    enabled,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const register = useMutation({
    mutationFn: async () => {
      const status = await requestPermission();
      if (status !== AUTHORIZATION_STATUS.AUTHORIZED && status !== AUTHORIZATION_STATUS.PROVISIONAL) {
        throw new PermissionDeniedError();
      }
      const token = await getToken();
      await deviceTokenApi.registerToken(token, getDevicePlatform());
    },
    onSuccess: () => {
      queryClient.setQueryData<PushNotificationStatus>(STATUS_QUERY_KEY, {
        permission: "granted",
        registered: true,
      });
    },
    onError: (error) => {
      if (error instanceof PermissionDeniedError) {
        void queryClient.invalidateQueries({ queryKey: STATUS_QUERY_KEY });
      }
    },
  });

  const unregister = useMutation({
    mutationFn: () => teardownDeviceToken(deviceTokenApi),
    onSuccess: () => {
      queryClient.setQueryData<PushNotificationStatus>(STATUS_QUERY_KEY, {
        permission: "granted",
        registered: false,
      });
    },
  });

  return { isRegistered, register, unregister };
}

export class PermissionDeniedError extends Error {
  constructor() {
    super("permission_denied");
    this.name = "PermissionDeniedError";
  }
}
